// Renders the outcome of the LOCAL recomputation done in crashFairness.js.
// Every row's state comes from a comparison this browser made; none of it is a
// flag the backend sent.
//
// A step can be:
//   pass        — recomputed value matched what the server recorded
//   fail        — recomputed value did NOT match (the server's number is wrong)
//   pending     — the input needed for this check is not published yet (the global
//                 server seed is still active), so it is neither proven nor broken
//   interrupted — the round was cut short by a server restart, so it settled at
//                 the last multiplier seen rather than the one its seed derives.
//                 The seed chain still proves the intended result was committed
//                 in advance, so this is reported plainly rather than as a failure.

const STEP_ICON = {
  pass: '✓',
  fail: '✕',
  pending: '•',
  interrupted: '!',
}

const Step = ({ status, children }) => (
  <li className={`standard-normal-provably-fair-check is-${status}`}>
    <span className="standard-normal-provably-fair-check-icon">{STEP_ICON[status]}</span>
    <span className="standard-normal-provably-fair-check-text">{children}</span>
  </li>
)

const VerificationSteps = ({ verification, round }) => {
  if (!verification) return null

  if (verification.error) {
    return <p className="standard-normal-provably-fair-error">{verification.error}</p>
  }

  const { commitment, roundSeed, crashPoint, verified, version, clientSeeds } = verification

  // A v2 round's round seed is derived from the three client seeds as well as the
  // server seed, so step 2 is what proves the published seeds are the ones that
  // were used. Saying so is the difference between "the server's maths is
  // self-consistent" and "my seed went into this result".
  const isV2 = Number(version) === 2
  const playerSeedCount = (clientSeeds ?? []).filter((entry) => entry.source === 'PLAYER').length
  const isPending = commitment.status === 'pending' || roundSeed.status === 'pending'
  const wasInterrupted = crashPoint.status === 'interrupted'

  const bannerClass = !verified
    ? 'is-failed'
    : wasInterrupted || isPending
      ? 'is-partial'
      : 'is-verified'

  const bannerText = !verified
    ? 'Verification failed'
    : wasInterrupted
      ? 'Round interrupted — seed verified, settled early'
      : isPending
        ? 'Crash point verified in your browser — seed reveal pending'
        : 'Round is provably fair'

  return (
    <div className="standard-normal-provably-fair-result">
      <ul className="standard-normal-provably-fair-checks">
        {/* Plain sentences on purpose. These three lines are the answer a player
            came for, so they say what was checked, not which primitive did it —
            the formula is in the section at the bottom of the screen and in the
            copyable block for anyone who wants it. */}
        <Step status={commitment.status}>
          {commitment.status === 'pending'
            ? 'Server seed not published yet — nothing to check it against'
            : commitment.status === 'pass'
              ? 'Server seed matches the hash published before the round'
              : 'Server seed does NOT match the hash published before the round'}
        </Step>

        <Step status={roundSeed.status}>
          {roundSeed.status === 'pending'
            ? 'This check needs the server seed first'
            : roundSeed.status === 'pass'
              ? isV2
                ? `Round seed comes from that server seed, this round ID and the ${clientSeeds?.length ?? 3} client seeds shown above`
                : 'Round seed comes from that server seed and this round ID'
              : isV2
                ? 'Round seed does NOT come from the published server seed and client seeds'
                : 'Round seed does NOT come from the published server seed'}
        </Step>

        <Step status={crashPoint.status}>
          {crashPoint.status === 'pass'
            ? `Crash point recomputed as ${crashPoint.actual?.toFixed(2)}x — matches ${round.crashPoint}`
            : crashPoint.status === 'interrupted'
              ? `Seed derives ${crashPoint.actual?.toFixed(2)}x, but a server restart ended this round early at ${round.crashPoint}`
              : `Recomputed ${crashPoint.actual?.toFixed(2)}x but the round recorded ${round.crashPoint}`}
        </Step>
      </ul>

      {/* Says how much of the entropy came from players, without editorialising.
          A round with no player seeds is exactly as verifiable — the difference is
          who contributed to it, which is the one thing v2 changed. */}
      {isV2 && (
        <p className="standard-normal-provably-fair-note">
          {playerSeedCount === 0
            ? 'No player sent a seed for this round, so all three were generated by the server. Every one of them is published above.'
            : `${playerSeedCount} of the 3 client seeds came from players in this round${
                playerSeedCount < 3 ? '; the rest were generated by the server' : ''
              }.`}
        </p>
      )}

      {wasInterrupted && (
        <p className="standard-normal-provably-fair-note">
          This round did not finish normally. Bets were settled at the last
          multiplier shown before the restart. The published seed still proves the
          result was fixed before betting opened.
        </p>
      )}

      <div className={`standard-normal-provably-fair-verdict ${bannerClass}`}>{bannerText}</div>

      <div className="standard-normal-provably-fair-intermediates">
        <div className="standard-normal-provably-fair-meta">
          <span className="standard-normal-provably-fair-meta-label">HMAC (hex)</span>
          <code className="standard-normal-provably-fair-meta-value is-mono">
            {verification.hex ?? '—'}
          </code>
        </div>
        <div className="standard-normal-provably-fair-meta">
          <span className="standard-normal-provably-fair-meta-label">Raw outcome</span>
          <span className="standard-normal-provably-fair-meta-value">
            {verification.rawOutcome ?? '—'}
          </span>
        </div>
      </div>
    </div>
  )
}

export default VerificationSteps
